"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { useFirebase } from './FirebaseContext';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Get admin emails from environment variable or use default
const ADMIN_EMAILS: string[] = process.env.NEXT_PUBLIC_ADMIN_EMAILS
  ? process.env.NEXT_PUBLIC_ADMIN_EMAILS.split(',').map(email => email.trim())
  : ['tannenboden.com@gmail.com']; // Fallback to default if not set

export function AuthProvider({ children }: { children: ReactNode }) {
  const { auth } = useFirebase();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      // Check if the user is an admin
      if (currentUser && currentUser.email) {
        setIsAdmin(ADMIN_EMAILS.includes(currentUser.email));
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();

      // Add browser compatibility scopes
      provider.addScope('profile');
      provider.addScope('email');

      // Set custom parameters to handle third-party cookie restrictions
      provider.setCustomParameters({
        // Only prompt once rather than every time
        prompt: 'select_account',
        // Handle Chrome's third-party cookie restrictions
        cookie_policy: 'none'
      });

      // Use signInWithPopup with better error handling
      try {
        await signInWithPopup(auth, provider);
      } catch (popupError: any) {
        console.warn('Popup sign-in error:', popupError);

        // Handle different types of authentication errors
        if (
          popupError.code === 'auth/popup-closed-by-user' ||
          popupError.code === 'auth/popup-blocked' ||
          popupError.code === 'auth/cancelled-popup-request'
        ) {
          // User-friendly message about popup issues
          alert(
            'Authentication popup was closed or blocked. ' +
            'Please ensure popups are allowed and third-party cookies are enabled ' +
            'for authentication.'
          );
        }
        else if (popupError.message && popupError.message.includes('invalid')) {
          // Handle domain authorization issues
          alert(
            'This application is not authorized to use Firebase Authentication from this domain. ' +
            'If you are running in development mode, please make sure localhost is added ' +
            'to the authorized domains in your Firebase Console Authentication settings.'
          );
          console.error('Firebase domain authorization error:', popupError);
          // Don't try redirect for domain authorization issues as it will also fail
          return;
        }
        else {
          // For other general errors
          alert('Authentication error occurred. Please try again later.');
          throw popupError;
        }
      }
    } catch (error) {
      console.error('Error signing in with Google:', error);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    logout,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}